import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '../../../../services/api';

import useAuth from '../../../../hooks/useAuth';

export default function ReferralSourceForm() {
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const { id } = useParams();
    const isEdit = Boolean(id);
    const [form, setForm] = useState({ facility_name: '', contact_person: '', phone: '', email: '', address: '', notes: '' });
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isAdmin) {
            navigate('/admin/referral-sources');
            return;
        }
        if (isEdit) {
            api.get(`/referral-sources/${id}`)
                .then(r => setForm({ facility_name: r.data.facility_name || '', contact_person: r.data.contact_person || '', phone: r.data.phone || '', email: r.data.email || '', address: r.data.address || '', notes: r.data.notes || '' }))
                .catch(() => toast.error('Failed to load'))
                .finally(() => setLoading(false));
        }
    }, [id, isEdit, isAdmin, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (isEdit) await api.put(`/referral-sources/${id}`, form);
            else await api.post('/referral-sources', form);
            toast.success(isEdit ? 'Source updated' : 'Source created');
            setTimeout(() => navigate('/admin/referral-sources'), 800);
        } catch {
            toast.error('Failed to save');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="flex justify-center items-center min-h-screen"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>;

    const fields = [
        { label: 'Facility Name', name: 'facility_name', required: true },
        { label: 'Contact Person', name: 'contact_person' },
        { label: 'Phone', name: 'phone' },
        { label: 'Email', name: 'email', type: 'email' },
    ];

    return (
        <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
            <Toaster position="top-right" richColors />
            <div className="max-w-xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                    <button onClick={() => navigate('/admin/referral-sources')} className="p-2 hover:bg-gray-200 rounded-lg"><ArrowLeft className="w-5 h-5" /></button>
                    <h2 className="text-2xl font-bold text-gray-900">{isEdit ? 'Edit Referral Source' : 'Add Referral Source'}</h2>
                </div>
                <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {fields.map(f => (
                            <div key={f.name}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}{f.required && <span className="text-red-500 ml-1">*</span>}</label>
                                <input 
                                    type={f.type || 'text'} 
                                    value={form[f.name]} 
                                    onChange={e => setForm(p => ({ ...p, [f.name]: e.target.value }))} 
                                    required={f.required} 
                                    style={{ color: '#000000', backgroundColor: '#ffffff' }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
                                />
                            </div>
                        ))}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                        <textarea 
                            value={form.address} 
                            onChange={e => setForm(p => ({ ...p, address: e.target.value }))} 
                            rows={2} 
                            style={{ color: '#000000', backgroundColor: '#ffffff' }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <textarea 
                            value={form.notes} 
                            onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} 
                            rows={3} 
                            style={{ color: '#000000', backgroundColor: '#ffffff' }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" 
                        />
                    </div>
                    <div className="flex gap-3 pt-2">
                        <button type="submit" disabled={saving} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition disabled:opacity-50">{saving ? 'Saving…' : isEdit ? 'Update' : 'Create'}</button>
                        <button type="button" onClick={() => navigate('/admin/referral-sources')} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
